<template>
  <div class="bg-neutral-50 min-h-screen py-8">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
      <!-- 头部 -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-neutral-900">
            <i class="fa fa-bell-o mr-2 text-primary"></i>消息中心
          </h1>
          <p class="text-neutral-500 text-sm mt-1">
            平台通知、版本更新与你的反馈对话
          </p>
        </div>
        <button
          @click="openFeedback"
          class="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 apple-transition"
        >
          <i class="fa fa-comment-o mr-1"></i>我要反馈
        </button>
      </div>

      <!-- Tab -->
      <div class="flex gap-2 bg-white rounded-xl p-1 shadow-sm border border-neutral-200 mb-5 w-fit">
        <button
          v-for="t in tabs"
          :key="t.id"
          @click="switchTab(t.id)"
          class="px-5 py-2 rounded-lg text-sm font-medium transition-colors"
          :class="currentTab === t.id ? 'bg-primary text-white' : 'text-neutral-500 hover:text-neutral-700'"
        >
          <i :class="'fa ' + t.icon + ' mr-1'"></i>{{ t.name }}
          <span
            v-if="t.id === 'notice' && unread.notice > 0"
            class="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[11px]"
            >{{ unread.notice }}</span
          >
          <span
            v-if="t.id === 'apply' && unread.apply > 0"
            class="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[11px]"
            >{{ unread.apply }}</span
          >
        </button>
      </div>

      <!-- ===== 平台通知 ===== -->
      <div v-if="currentTab === 'notice'">
        <div
          v-if="noticeList.length > 0"
          class="flex justify-end mb-3"
        >
          <button
            @click="readAll"
            class="text-sm text-primary hover:opacity-80"
          >
            <i class="fa fa-check-circle-o mr-1"></i>全部标为已读
          </button>
        </div>

        <div v-loading="loading" style="min-height: 160px" class="space-y-3">
          <div
            v-for="n in noticeList"
            :key="n.id"
            @click="readOne(n)"
            class="bg-white rounded-xl border p-4 cursor-pointer apple-transition hover:shadow-md"
            :class="n.isRead === 0 ? 'border-primary/40 bg-primary/[0.02]' : 'border-neutral-200'"
          >
            <div class="flex items-start gap-3">
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                :class="iconBg(n.type)"
              >
                <i :class="'fa ' + iconOf(n.type)"></i>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span
                    v-if="n.isRead === 0"
                    class="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                  ></span>
                  <h3 class="font-medium text-neutral-900 truncate">{{ n.title }}</h3>
                </div>
                <p class="text-sm text-neutral-600 mt-1 whitespace-pre-wrap">
                  {{ n.content }}
                </p>
                <div class="text-xs text-neutral-400 mt-2 flex items-center gap-3">
                  <span><i class="fa fa-clock-o mr-1"></i>{{ n.createTime }}</span>
                  <span v-if="n.senderName">
                    <i class="fa fa-user-o mr-1"></i>{{ n.senderName }}
                  </span>
                  <button
                    @click.stop="removeOne(n)"
                    class="ml-auto text-neutral-400 hover:text-red-500"
                  >
                    <i class="fa fa-trash-o"></i>删除
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!loading && noticeList.length === 0"
          class="text-center py-16 text-neutral-400 bg-white rounded-xl border border-neutral-200"
        >
          <i class="fa fa-bell-slash-o text-4xl mb-3"></i>
          <p>暂无通知</p>
        </div>

        <div v-if="noticeTotal > noticePageSize" class="flex justify-center mt-6">
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="noticePage"
            :page-size="noticePageSize"
            :total="noticeTotal"
            @current-change="onNoticePage"
          />
        </div>
      </div>

      <!-- ===== 我的反馈 ===== -->
      <div v-else>
        <div v-loading="applyLoading" style="min-height: 160px" class="space-y-3">
          <div
            v-for="a in applyList"
            :key="a.id"
            @click="openChat(a)"
            class="bg-white rounded-xl border p-4 cursor-pointer apple-transition hover:shadow-md"
            :class="a.userRead === 0 ? 'border-primary/40 bg-primary/[0.02]' : 'border-neutral-200'"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 flex-wrap">
                  <span
                    v-if="a.userRead === 0"
                    class="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                  ></span>
                  <span class="font-medium text-neutral-900">{{ a.title }}</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600"
                    >{{ applyTypeText(a.type) }}</span
                  >
                </div>
                <p class="text-sm text-neutral-500 mt-1 line-clamp-2">{{ a.content }}</p>
                <div
                  v-if="a.reply"
                  class="mt-2 text-sm bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-neutral-700"
                >
                  <span class="text-green-700 font-medium">平台回复：</span>{{ a.reply }}
                </div>
                <div class="text-xs text-neutral-400 mt-2 flex items-center gap-3">
                  <span><i class="fa fa-clock-o mr-1"></i>{{ a.updateTime || a.createTime }}</span>
                  <span :class="statusClass(a.status)">{{ statusText(a.status) }}</span>
                  <span v-if="a.closed === 1" class="text-neutral-400">· 已结束</span>
                </div>
              </div>
              <i class="fa fa-chevron-right text-neutral-300"></i>
            </div>
          </div>
        </div>

        <div
          v-if="!applyLoading && applyList.length === 0"
          class="text-center py-16 text-neutral-400 bg-white rounded-xl border border-neutral-200"
        >
          <i class="fa fa-comment-o text-4xl mb-3"></i>
          <p>还没有提交过反馈</p>
          <button
            @click="openFeedback"
            class="mt-3 px-4 py-2 rounded-lg bg-primary text-white text-sm"
          >
            去反馈
          </button>
        </div>
      </div>
    </div>

    <!-- ===== 对话弹窗 ===== -->
    <el-dialog
      :title="chat.apply ? chat.apply.title : '对话'"
      v-model="chatVisible"
      width="560px"
      :close-on-click-modal="false"
    >
      <div class="max-h-[52vh] overflow-y-auto space-y-3 pr-1" ref="chatBox">
        <div
          v-for="(m, i) in chat.list"
          :key="i"
          class="flex"
          :class="m.role === 'admin' ? 'justify-start' : 'justify-end'"
        >
          <div
            class="max-w-[78%] rounded-2xl px-4 py-2.5 text-sm"
            :class="
              m.role === 'admin'
                ? 'bg-neutral-100 text-neutral-800 rounded-bl-md'
                : 'bg-primary text-white rounded-br-md'
            "
          >
            <div
              v-if="m.role === 'admin'"
              class="text-xs text-green-700 font-medium mb-1"
            >
              {{ m.senderName || '平台' }}
            </div>
            <div class="whitespace-pre-wrap break-words">{{ m.content }}</div>
            <div
              class="text-[11px] mt-1"
              :class="m.role === 'admin' ? 'text-neutral-400' : 'text-white/70'"
            >
              {{ m.createTime }}
            </div>
          </div>
        </div>
        <div v-if="chat.list.length === 0" class="text-center text-neutral-400 text-sm py-8">
          暂无对话内容
        </div>
      </div>

      <div class="mt-4 flex gap-2" v-if="chat.apply && chat.apply.closed !== 1">
        <input
          v-model="chatInput"
          @keyup.enter="sendMsg"
          maxlength="1000"
          placeholder="继续说明你的问题…"
          class="flex-1 px-4 py-2.5 rounded-lg border border-neutral-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
        />
        <button
          @click="sendMsg"
          :disabled="sending"
          class="px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {{ sending ? '发送中' : '发送' }}
        </button>
      </div>
      <div v-else class="mt-4 text-sm text-neutral-400">
        该对话已结束，如需继续请重新提交反馈。
      </div>
    </el-dialog>

    <!-- ===== 提交反馈弹窗 ===== -->
    <el-dialog title="提交反馈" v-model="feedbackVisible" width="520px">
      <div class="space-y-4">
        <div>
          <label class="block text-sm text-neutral-600 mb-1">反馈类型</label>
          <select
            v-model="fb.type"
            class="w-full px-3 py-2.5 rounded-lg border border-neutral-300 outline-none focus:border-primary text-sm"
          >
            <option v-for="t in fbTypes" :key="t.value" :value="t.value">
              {{ t.label }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm text-neutral-600 mb-1">标题</label>
          <input
            v-model="fb.title"
            maxlength="50"
            placeholder="一句话说明问题"
            class="w-full px-3 py-2.5 rounded-lg border border-neutral-300 outline-none focus:border-primary text-sm"
          />
        </div>
        <div>
          <label class="block text-sm text-neutral-600 mb-1">详细内容</label>
          <textarea
            v-model="fb.content"
            rows="5"
            maxlength="1000"
            placeholder="请描述你遇到的问题或建议…"
            class="w-full px-3 py-2.5 rounded-lg border border-neutral-300 outline-none focus:border-primary text-sm resize-none"
          ></textarea>
        </div>
      </div>
      <template #footer>
        <button
          @click="feedbackVisible = false"
          class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 text-sm"
        >
          取消
        </button>
        <button
          @click="submitFeedback"
          :disabled="fbSending"
          class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {{ fbSending ? '提交中' : '提交' }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { requireLogin } from "../utils/authGuard";

export default {
  name: "MessagesPage",
  data() {
    return {
      currentTab: "notice",
      tabs: [
        { id: "notice", name: "平台通知", icon: "fa-bell-o" },
        { id: "apply", name: "我的反馈", icon: "fa-comments-o" },
      ],
      // 通知
      noticeList: [],
      noticePage: 1,
      noticePageSize: 10,
      noticeTotal: 0,
      loading: false,
      // 反馈
      applyList: [],
      applyLoading: false,
      // 对话
      chatVisible: false,
      chat: { apply: null, list: [] },
      chatInput: "",
      sending: false,
      // 提交反馈
      feedbackVisible: false,
      fbSending: false,
      fb: { type: "feedback", title: "", content: "" },
      fbTypes: [
        { value: "feedback", label: "意见反馈" },
        { value: "account", label: "账号问题" },
        { value: "points", label: "创值申诉" },
        { value: "publish", label: "发布工具权限" },
        { value: "other", label: "其他" },
      ],
      // 未读
      unread: { notice: 0, apply: 0, total: 0 },
      timer: null,
    };
  },
  created() {
    if (!requireLogin(this, "消息中心")) return;
    this.loadUnread();
    this.loadNotices();
    this.loadApplies();
    // 未读数定时刷新，保证平台回复能及时提醒
    this.timer = setInterval(() => this.loadUnread(), 60000);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    switchTab(id) {
      this.currentTab = id;
      if (id === "apply") this.loadApplies();
    },
    async loadUnread() {
      try {
        const res = await this.$axios({ url: "notice/unread", method: "GET" });
        if (res.code === 200 && res.data) {
          this.unread = res.data;
        }
      } catch (e) {
        /* 静默 */
      }
    },
    async loadNotices() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.noticePage,
          pageSize: this.noticePageSize,
        });
        const res = await this.$axios({
          url: "notice/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200 && res.data) {
          this.noticeList = res.data.list || [];
          this.noticeTotal = res.data.total || 0;
        }
      } catch (e) {
        ElMessage({ message: "加载通知失败", type: "error" });
      }
      this.loading = false;
    },
    onNoticePage(p) {
      this.noticePage = p;
      this.loadNotices();
    },
    async readOne(n) {
      if (n.isRead === 0) {
        try {
          await this.$axios({ url: "notice/read/" + n.id, method: "GET" });
          n.isRead = 1;
          this.loadUnread();
        } catch (e) {
          /* ignore */
        }
      }
      // 反馈回复类通知：直接打开对应对话
      if (n.relatedId) {
        this.$axios({
          url: "notice/apply/msgs",
          method: "POST",
          data: { data: await this.$aesEncrypt({ applyId: n.relatedId }) },
        })
          .then((res) => {
            if (res.code === 200 && res.data) {
              this.chat = res.data;
              this.chatVisible = true;
              this.scrollChat();
            }
          })
          .catch(() => {});
      }
    },
    async readAll() {
      try {
        await this.$axios({ url: "notice/readall", method: "POST" });
        this.loadNotices();
        this.loadUnread();
        ElMessage({ message: "已全部标记为已读", type: "success" });
      } catch (e) {
        ElMessage({ message: "操作失败", type: "error" });
      }
    },
    async removeOne(n) {
      try {
        await this.$axios({ url: "notice/delete/" + n.id, method: "DELETE" });
        this.loadNotices();
        this.loadUnread();
      } catch (e) {
        ElMessage({ message: "删除失败", type: "error" });
      }
    },
    async loadApplies() {
      this.applyLoading = true;
      try {
        const res = await this.$axios({
          url: "notice/apply/list",
          method: "POST",
          data: { data: await this.$aesEncrypt({}) },
        });
        if (res.code === 200 && res.data) {
          this.applyList = res.data.list || [];
        }
      } catch (e) {
        ElMessage({ message: "加载反馈失败", type: "error" });
      }
      this.applyLoading = false;
    },
    async openChat(a) {
      try {
        const res = await this.$axios({
          url: "notice/apply/msgs",
          method: "POST",
          data: { data: await this.$aesEncrypt({ applyId: a.id }) },
        });
        if (res.code === 200 && res.data) {
          this.chat = res.data;
          this.chatInput = "";
          this.chatVisible = true;
          a.userRead = 1;
          this.loadUnread();
          this.scrollChat();
        }
      } catch (e) {
        ElMessage({ message: "加载对话失败", type: "error" });
      }
    },
    scrollChat() {
      this.$nextTick(() => {
        const box = this.$refs.chatBox;
        if (box) box.scrollTop = box.scrollHeight;
      });
    },
    async sendMsg() {
      const content = (this.chatInput || "").trim();
      if (!content) return;
      this.sending = true;
      try {
        const res = await this.$axios({
          url: "notice/apply/send",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({
              applyId: this.chat.apply.id,
              content,
            }),
          },
        });
        if (res.code === 200) {
          this.chat.list.push({
            role: "user",
            content,
            senderName: "我",
            createTime: new Date().toLocaleString(),
          });
          this.chatInput = "";
          this.scrollChat();
          this.loadApplies();
        }
      } catch (e) {
        ElMessage({ message: "发送失败", type: "error" });
      }
      this.sending = false;
    },
    openFeedback() {
      this.fb = { type: "feedback", title: "", content: "" };
      this.feedbackVisible = true;
    },
    async submitFeedback() {
      if (!this.fb.title.trim() || !this.fb.content.trim()) {
        ElMessage({ message: "请填写标题与内容", type: "warning" });
        return;
      }
      this.fbSending = true;
      try {
        const res = await this.$axios({
          url: "user/apply/submit",
          method: "POST",
          data: { data: await this.$aesEncrypt({ ...this.fb }) },
        });
        if (res.code === 200) {
          ElMessage({ message: "提交成功，平台会尽快回复", type: "success" });
          this.feedbackVisible = false;
          this.currentTab = "apply";
          this.loadApplies();
        } else {
          ElMessage({ message: res.msg || "提交失败", type: "error" });
        }
      } catch (e) {
        ElMessage({ message: "提交失败", type: "error" });
      }
      this.fbSending = false;
    },
    iconOf(type) {
      if (type === "update") return "fa-rocket";
      if (type === "reply") return "fa-reply";
      if (type === "announce") return "fa-bullhorn";
      return "fa-info-circle";
    },
    iconBg(type) {
      if (type === "update") return "bg-purple-50 text-purple-600";
      if (type === "reply") return "bg-green-50 text-green-600";
      if (type === "announce") return "bg-orange-50 text-orange-500";
      return "bg-blue-50 text-primary";
    },
    applyTypeText(t) {
      const map = {
        publish: "发布权限",
        points: "创值申诉",
        account: "账号问题",
        feedback: "意见反馈",
        other: "其他",
      };
      return map[t] || "其他";
    },
    statusText(s) {
      if (s === 1) return "已处理";
      if (s === 2) return "已驳回";
      return "待处理";
    },
    statusClass(s) {
      if (s === 1) return "text-green-600";
      if (s === 2) return "text-red-500";
      return "text-orange-500";
    },
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
